import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AntLocalization from '../../../utils/AntLocalization';
import AntAPI from '../../../utils/AntAPI';
import AntCondition from '../../../utils/AntCondition';
import AntAuth from '../../../utils/AntAuth';
import { SidebarState, SidebarAvatar, sidebarDate } from './SidebarStandard';

// Same organization as ante-mobile-user DashboardApproval:
// switcher Đề xuất / Chứng từ; proposals open OFApproval, documents open the voucher itself.
// Status / document-type names copied from ante-mobile-user home-v2/ApprovalPresentation.js.
// Kept in the kit on purpose: web field configs for "of"/"wf" load lazily, so the sidebar
// opened from another module would otherwise show raw codes (Approving, EXPOrder).
var TEXT = {
    requests: { vi: 'Đề xuất', en: 'Requests', fr: 'Demandes', es: 'Solicitudes', id: 'Pengajuan', ja: '申請', zh: '申请', ko: '요청', hi: 'अनुरोध', ar: 'الطلبات' },
    documents: { vi: 'Chứng từ', en: 'Documents', fr: 'Documents', es: 'Documentos', id: 'Dokumen', ja: '伝票', zh: '单据', ko: '문서', hi: 'दस्तावेज़', ar: 'المستندات' },
    all: { vi: 'Tất cả', en: 'All', fr: 'Tous', es: 'Todos', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' },
    approve: { vi: 'Phê duyệt', en: 'Approve', fr: 'Approuver', es: 'Aprobar', id: 'Setujui', ja: '承認', zh: '批准', ko: '승인', hi: 'अनुमोदित करें', ar: 'موافقة' },
    reject: { vi: 'Từ chối', en: 'Reject', fr: 'Rejeter', es: 'Rechazar', id: 'Tolak', ja: '却下', zh: '拒绝', ko: '거부', hi: 'अस्वीकार करें', ar: 'رفض' },
    newRequest: { vi: 'Tạo đề xuất', en: 'New request', fr: 'Nouvelle demande', es: 'Nueva solicitud', id: 'Buat pengajuan', ja: '新規申請', zh: '新建申请', ko: '새 요청', hi: 'नया अनुरोध', ar: 'طلب جديد' },
    untitled: { vi: 'Đề xuất chưa có tiêu đề', en: 'Untitled request', fr: 'Demande sans titre', es: 'Solicitud sin título', id: 'Pengajuan tanpa judul', ja: '無題の申請', zh: '未命名申请', ko: '제목 없는 요청', hi: 'बिना शीर्षक का अनुरोध', ar: 'طلب بدون عنوان' },
    noRequests: { vi: 'Chưa có đề xuất', en: 'No requests yet', fr: 'Aucune demande', es: 'Sin solicitudes', id: 'Belum ada pengajuan', ja: '申請はありません', zh: '暂无申请', ko: '요청이 없습니다', hi: 'अभी कोई अनुरोध नहीं', ar: 'لا توجد طلبات' },
    noDocuments: { vi: 'Chưa có chứng từ phê duyệt', en: 'No approval documents', fr: 'Aucun document à approuver', es: 'Sin documentos de aprobación', id: 'Belum ada dokumen persetujuan', ja: '承認伝票はありません', zh: '暂无审批单据', ko: '승인 문서가 없습니다', hi: 'कोई अनुमोदन दस्तावेज़ नहीं', ar: 'لا توجد مستندات للموافقة' },
    noViewPermission: { vi: 'Bạn chưa có quyền xem phiếu', en: 'No permission to view', fr: 'Accès au document refusé', es: 'Sin permiso para ver', id: 'Belum berhak melihat', ja: '閲覧権限がありません', zh: '无权查看该单据', ko: '열람 권한이 없습니다', hi: 'देखने की अनुमति नहीं', ar: 'لا صلاحية للعرض' },
    loadMore: { vi: 'Xem thêm', en: 'Load more', fr: 'Voir plus', es: 'Ver más', id: 'Muat lagi', ja: 'さらに表示', zh: '加载更多', ko: '더 보기', hi: 'और देखें', ar: 'عرض المزيد' },
    level: { vi: 'Cấp', en: 'Level', fr: 'Niveau', es: 'Nivel', id: 'Tingkat', ja: 'レベル', zh: '级别', ko: '단계', hi: 'स्तर', ar: 'المستوى' },
    loadFailed: { vi: 'Chưa tải được danh sách', en: 'List could not be loaded', fr: 'Impossible de charger la liste', es: 'No se pudo cargar la lista', id: 'Daftar tidak dapat dimuat', ja: '一覧を読み込めませんでした', zh: '无法加载列表', ko: '목록을 불러오지 못했습니다', hi: 'सूची लोड नहीं हो सकी', ar: 'تعذر تحميل القائمة' },
    createFailed: { vi: 'Không tạo được đề xuất', en: 'Could not create request', fr: 'Impossible de créer la demande', es: 'No se pudo crear la solicitud', id: 'Pengajuan tidak dapat dibuat', ja: '申請を作成できませんでした', zh: '无法创建申请', ko: '요청을 만들 수 없습니다', hi: 'अनुरोध नहीं बनाया जा सका', ar: 'تعذر إنشاء الطلب' },
    decideFailed: { vi: 'Không xử lý được đề xuất', en: 'Could not process the request', fr: 'Impossible de traiter la demande', es: 'No se pudo procesar la solicitud', id: 'Pengajuan tidak dapat diproses', ja: '申請を処理できませんでした', zh: '无法处理申请', ko: '요청을 처리할 수 없습니다', hi: 'अनुरोध संसाधित नहीं हो सका', ar: 'تعذرت معالجة الطلب' }
};

var APPROVAL_STATUS = {
    New: { vi: 'Nháp', en: 'Draft', fr: 'Brouillon', es: 'Borrador', id: 'Draf', ja: '下書き', zh: '草稿', ko: '초안', hi: 'ड्राफ्ट', ar: 'مسودة' },
    Cancel: { vi: 'Đã hủy', en: 'Cancelled', fr: 'Annulé', es: 'Cancelado', id: 'Dibatalkan', ja: '取消済み', zh: '已取消', ko: '취소됨', hi: 'रद्द', ar: 'ملغى' },
    Close: { vi: 'Đã đóng', en: 'Closed', fr: 'Clôturé', es: 'Cerrado', id: 'Ditutup', ja: '完了', zh: '已关闭', ko: '종료됨', hi: 'बंद', ar: 'مغلق' },
    Approving: { vi: 'Chờ duyệt', en: 'Pending', fr: 'En attente', es: 'Pendiente', id: 'Menunggu', ja: '承認待ち', zh: '待审批', ko: '승인 대기', hi: 'लंबित', ar: 'قيد الانتظار' },
    Pending: { vi: 'Chờ duyệt', en: 'Pending', fr: 'En attente', es: 'Pendiente', id: 'Menunggu', ja: '承認待ち', zh: '待审批', ko: '승인 대기', hi: 'लंबित', ar: 'قيد الانتظار' },
    Approved: { vi: 'Đã duyệt', en: 'Approved', fr: 'Approuvé', es: 'Aprobado', id: 'Disetujui', ja: '承認済み', zh: '已批准', ko: '승인됨', hi: 'स्वीकृत', ar: 'تمت الموافقة' },
    Rejected: { vi: 'Từ chối', en: 'Rejected', fr: 'Rejeté', es: 'Rechazado', id: 'Ditolak', ja: '却下', zh: '已拒绝', ko: '거부됨', hi: 'अस्वीकृत', ar: 'مرفوض' },
    Invalidated: { vi: 'Cần trình lại', en: 'Resubmit', fr: 'À resoumettre', es: 'Reenviar', id: 'Ajukan ulang', ja: '再申請', zh: '需重新提交', ko: '재제출 필요', hi: 'पुनः जमा करें', ar: 'إعادة التقديم' }
};
var STATUS_TONE = { New: 'neutral', Cancel: 'neutral', Close: 'neutral', Invalidated: 'neutral', Approving: 'warning', Pending: 'warning', Approved: 'success', Rejected: 'danger' };

var DOCUMENT_TYPES = {
    EXPOrder: { vi: 'Phiếu chi tiêu', en: 'Expense', fr: 'Dépense', es: 'Gasto', id: 'Pengeluaran', ja: '経費', zh: '费用单', ko: '지출', hi: 'व्यय', ar: 'مصروف' },
    AROrder: { vi: 'Đơn bán hàng', en: 'Sales order', fr: 'Commande client', es: 'Pedido de venta', id: 'Pesanan penjualan', ja: '受注', zh: '销售订单', ko: '판매 주문', hi: 'बिक्री आदेश', ar: 'أمر بيع' },
    APOrder: { vi: 'Đơn mua hàng', en: 'Purchase order', fr: 'Bon de commande', es: 'Orden de compra', id: 'Pesanan pembelian', ja: '発注', zh: '采购订单', ko: '구매 주문', hi: 'क्रय आदेश', ar: 'أمر شراء' },
    APRequest: { vi: 'Đề nghị mua', en: 'Purchase request', fr: "Demande d'achat", es: 'Solicitud de compra', id: 'Permintaan beli', ja: '購買依頼', zh: '采购申请', ko: '구매 요청', hi: 'क्रय अनुरोध', ar: 'طلب شراء' },
    CARequest: { vi: 'Đề nghị chi', en: 'Payment request', fr: 'Demande paiement', es: 'Solicitud de pago', id: 'Permintaan bayar', ja: '支払依頼', zh: '付款申请', ko: '지급 요청', hi: 'भुगतान अनुरोध', ar: 'طلب دفع' },
    CAIssue: { vi: 'Phiếu chi', en: 'Payment', fr: 'Paiement', es: 'Pago', id: 'Pembayaran', ja: '支払', zh: '付款单', ko: '지급', hi: 'भुगतान', ar: 'دفعة' },
    CATransfer: { vi: 'Chuyển tiền', en: 'Cash transfer', fr: 'Virement de fonds', es: 'Transferencia', id: 'Transfer kas', ja: '資金移動', zh: '资金调拨', ko: '자금 이체', hi: 'नकद अंतरण', ar: 'تحويل نقدي' },
    ICTransfer: { vi: 'Chuyển kho', en: 'Stock transfer', fr: 'Transfert stock', es: 'Traslado de stock', id: 'Transfer stok', ja: '在庫移動', zh: '调拨单', ko: '재고 이동', hi: 'स्टॉक अंतरण', ar: 'نقل مخزون' },
    ICAdjust: { vi: 'Điều chỉnh kho', en: 'Stock adjustment', fr: 'Ajustement de stock', es: 'Ajuste de stock', id: 'Penyesuaian stok', ja: '在庫調整', zh: '库存调整', ko: '재고 조정', hi: 'स्टॉक समायोजन', ar: 'تسوية مخزون' },
    ARReturn: { vi: 'Hàng bán trả lại', en: 'Sales return', fr: 'Retour client', es: 'Devolución de venta', id: 'Retur penjualan', ja: '売上返品', zh: '销售退货', ko: '판매 반품', hi: 'बिक्री वापसी', ar: 'مرتجع مبيعات' },
    FAAssetRegister: { vi: 'Ghi tăng tài sản', en: 'Asset registration', fr: "Enregistrement d'actif", es: 'Alta de activo', id: 'Pendaftaran aset', ja: '資産登録', zh: '资产登记', ko: '자산 등록', hi: 'परिसंपत्ति पंजीकरण', ar: 'تسجيل أصل' }
};

// API group of each approval document (same services the voucher screens call).
var DOCUMENT_API_GROUP = {
    AROrder: 'ar', ARReturn: 'ar', APOrder: 'ap', APRequest: 'ap', CAIssue: 'ca', CARequest: 'ca', CATransfer: 'ca',
    EXPOrder: 'exp', ICTransfer: 'inv', ICAdjust: 'inv', FAAssetRegister: 'fa'
};

var REQUEST_FILTERS = ['all', 'Approving', 'Approved', 'Rejected', 'New'];

var REQUEST_PAGE_SIZE = 20;
var DOCUMENT_PAGE_SIZE = 30;

function errorText(value, fallback) {
    if (typeof value === 'string' && value) return value;
    return (value && (value.content || value.message)) || AntLocalization.translateText(fallback);
}

function StatusBadge(props) {
    var label = APPROVAL_STATUS[props.status] || props.status;
    return <span className={'rs-badge rs-badge--' + (STATUS_TONE[props.status] || 'neutral')}>{AntLocalization.translateText(label)}</span>;
}

function useCanSee() {
    return useMemo(function () {
        var can = function (whenUser) {
            return AntCondition.isVisible(null, { visible: { whenSys: { use_Module_Approval: 1 }, whenUser: whenUser } });
        };
        return {
            request: can({ Approval_request_approval: 1 }),
            approve: can({ Approval_can_approve: 1 }),
            documents: can({ WFApprovalPending_can_approve: 1 })
        };
    }, []);
}

// ─── Đề xuất (OFApproval) ─────────────────────────────────────────
function ProposalList(props) {
    var navigate = props.navigate;
    var api = useMemo(function () {
        return new AntAPI('of', 'OFApproval');
    }, []);
    var [filter, setFilter] = useState('all');
    var [rows, setRows] = useState([]);
    var [loading, setLoading] = useState(false);
    var [hasMore, setHasMore] = useState(false);
    var [error, setError] = useState('');
    var [creating, setCreating] = useState(false);
    var [deciding, setDeciding] = useState(null);
    var pageRef = useRef(0);
    var busyRef = useRef(false);
    var creatingRef = useRef(false);
    var loadFailed = TEXT.loadFailed;

    var load = useCallback(
        function (refresh) {
            if (busyRef.current) return;
            busyRef.current = true;
            var pageIndex = refresh ? 0 : pageRef.current + 1;
            setLoading(true);
            setError('');
            var params = {
                pageSize: REQUEST_PAGE_SIZE,
                pageIndex: pageIndex,
                orderBy: 'created_at',
                orderType: 'desc',
                selectFields: ['id', 'code', 'title', 'description', 'approval_status', 'created_at', 'request_user_code', 'approver1_user_code', 'approver2_user_code']
            };
            if (filter !== 'all') params.approval_status = filter;
            // Participant scope (requester + approvers) is enforced by the service, same list as mobile.
            api.post('/' + api.routeURL + '/list', params, function (result) {
                busyRef.current = false;
                setLoading(false);
                if (!result || result.error) {
                    setError(errorText(result && result.error, loadFailed));
                    return;
                }
                var received = (result.data && result.data.rows) || result.rows || [];
                if (!(received instanceof Array)) received = [];
                pageRef.current = pageIndex;
                setHasMore(received.length === REQUEST_PAGE_SIZE);
                setRows(function (current) {
                    return refresh ? received : current.concat(received.filter(function (row) {
                        return !current.some(function (item) { return item.id === row.id; });
                    }));
                });
            });
        },
        [api, filter]
    );

    useEffect(
        function () {
            setRows([]);
            load(true);
        },
        [load]
    );

    // Quick decision kept on web (list + detail); BE re-checks approver/permission in actionCustom.
    var canDecide = function (item) {
        var me = props.currentUserCode;
        return props.canApprove && item.approval_status === 'Approving' && !!me && (item.approver1_user_code === me || item.approver2_user_code === me);
    };
    var decide = function (action, item, e) {
        e.stopPropagation();
        if (deciding) return;
        setDeciding(item.id);
        setError('');
        api.post('/' + api.routeURL + '/' + action, { id: item.id }, function (result) {
            setDeciding(null);
            if (!result || result.error) {
                setError(errorText(result && result.error, TEXT.decideFailed));
                return;
            }
            load(true);
        });
    };

    var createRequest = async function () {
        if (creatingRef.current) return;
        creatingRef.current = true;
        setCreating(true);
        setError('');
        try {
            var result = await api.postQuickCreate();
            var value = (result && result.data) || result;
            if (!result || result.error || !value || !value.code) {
                setError(errorText(result && result.error, TEXT.createFailed));
                return;
            }
            navigate('/of/OFApproval/' + encodeURIComponent(value.code));
        } catch (failure) {
            setError(errorText(failure, TEXT.createFailed));
        } finally {
            creatingRef.current = false;
            setCreating(false);
        }
    };

    return (
        <div className="rs-pane">
            <div className="rs-chips" role="tablist">
                {REQUEST_FILTERS.map(function (key) {
                    return (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={filter === key}
                            className={'rs-chip' + (filter === key ? ' active' : '')}
                            onClick={function () {
                                setFilter(key);
                            }}
                        >
                            {AntLocalization.translateText(key === 'all' ? TEXT.all : APPROVAL_STATUS[key])}
                        </button>
                    );
                })}
            </div>
            <div className="rs-list">
                {error && <SidebarState type="error" text={error} onRetry={function () { load(true); }} compact={rows.length > 0} />}
                {!loading && !error && rows.length === 0 && (
                    <SidebarState type="empty" icon="approval" text={TEXT.noRequests} />
                )}
                {rows.map(function (item) {
                    var profile = item.request_user_profile || {};
                    var creator = profile.display_name || item.request_user_name || item.request_user_code || '';
                    var open = function () {
                        navigate('/of/OFApproval/' + encodeURIComponent(item.code));
                    };
                    return (
                        <div
                            key={item.id}
                            role="button"
                            tabIndex={0}
                            className="rs-card approval-card"
                            onClick={open}
                            onKeyDown={function (e) {
                                if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    open();
                                }
                            }}
                        >
                            <div className="rs-card-row">
                                <span className="rs-card-code">#{item.code}</span>
                                <StatusBadge status={item.approval_status} />
                            </div>
                            <div className="rs-card-title">
                                {item.title || AntLocalization.translateText(TEXT.untitled)}
                            </div>
                            {item.description && <div className="rs-card-text">{item.description}</div>}
                            <div className="rs-card-footer">
                                <SidebarAvatar name={creator} src={profile.avatar_full_path} />
                                <div className="rs-card-person">
                                    <span className="rs-card-person-name">{creator}</span>
                                    <span className="rs-card-person-detail">{sidebarDate(item.created_at, true)}</span>
                                </div>
                            </div>
                            {canDecide(item) && (
                                <div className="approval-quick-actions">
                                    <button
                                        type="button"
                                        className="approval-quick-btn approval-quick-btn--reject"
                                        disabled={!!deciding}
                                        onClick={function (e) {
                                            decide('actionCustomReject', item, e);
                                        }}
                                    >
                                        {AntLocalization.translateText(TEXT.reject)}
                                    </button>
                                    <button
                                        type="button"
                                        className="approval-quick-btn approval-quick-btn--approve"
                                        disabled={!!deciding}
                                        onClick={function (e) {
                                            decide('actionCustomApprove', item, e);
                                        }}
                                    >
                                        {AntLocalization.translateText(TEXT.approve)}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {loading && <SidebarState type="loading" compact={rows.length > 0} />}
                {!loading && !error && hasMore && (
                    <button type="button" className="rs-more-btn" onClick={function () { load(false); }}>
                        {AntLocalization.translateText(TEXT.loadMore)}
                    </button>
                )}
            </div>
            {props.canCreate && (
                <button
                    type="button"
                    className="task-fab"
                    disabled={creating}
                    onClick={createRequest}
                    title={AntLocalization.translateText(TEXT.newRequest)}
                    aria-label={AntLocalization.translateText(TEXT.newRequest)}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                </button>
            )}
        </div>
    );
}

// ─── Chứng từ (WF approval rounds) ─────────────────────────────────
function DocumentList(props) {
    var onOpenDocument = props.onOpenDocument;
    var api = useMemo(function () {
        return new AntAPI('wf', 'WFApprovalActivity');
    }, []);
    var [rows, setRows] = useState([]);
    var [loading, setLoading] = useState(false);
    var [hasMore, setHasMore] = useState(false);
    var [error, setError] = useState('');
    var [deciding, setDeciding] = useState(null);
    var offsetRef = useRef(0);
    var busyRef = useRef(false);
    var loadFailed = TEXT.loadFailed;

    var load = useCallback(
        function (refresh) {
            if (busyRef.current) return;
            busyRef.current = true;
            setLoading(true);
            setError('');
            // includeHistory: latest round of each document the viewer created or approves (same as mobile).
            api.post(
                '/' + api.routeURL + '/rptMyPendings',
                { includeHistory: true, pageSize: DOCUMENT_PAGE_SIZE, offset: refresh ? 0 : offsetRef.current },
                function (result) {
                    busyRef.current = false;
                    setLoading(false);
                    var value = result && (result.data || result);
                    if (!result || result.error || !value || !(value.rows instanceof Array)) {
                        setError(errorText(result && result.error, loadFailed));
                        return;
                    }
                    offsetRef.current = value.nextOffset || 0;
                    setHasMore(value.hasMore === true);
                    setRows(function (current) {
                        if (refresh) return value.rows;
                        var merged = new Map();
                        current.concat(value.rows).forEach(function (row) {
                            merged.set(row.row_key, row);
                        });
                        return Array.from(merged.values());
                    });
                }
            );
        },
        [api]
    );

    useEffect(
        function () {
            load(true);
        },
        [load]
    );

    // Quick decision on the current level; the document service re-checks approver, level and content (executeApproval).
    var canDecide = function (row) {
        return row.request_status === 'Pending' && Number(row.can_decide) === 1 && !!DOCUMENT_API_GROUP[row.doc_type];
    };
    var decide = function (action, row, e) {
        e.stopPropagation();
        if (deciding) return;
        setDeciding(row.row_key);
        setError('');
        api.post('/' + DOCUMENT_API_GROUP[row.doc_type] + '/' + row.doc_type + '/' + action, { id: row.doc_id }, function (result) {
            setDeciding(null);
            if (!result || result.error) {
                setError(errorText(result && result.error, TEXT.decideFailed));
                return;
            }
            load(true);
        });
    };

    return (
        <div className="rs-pane">
            <div className="rs-list">
                {error && <SidebarState type="error" text={error} onRetry={function () { load(true); }} compact={rows.length > 0} />}
                {!loading && !error && rows.length === 0 && (
                    <SidebarState type="empty" icon="document" text={TEXT.noDocuments} />
                )}
                {rows.map(function (row) {
                    var typeLabel = DOCUMENT_TYPES[row.doc_type] ? AntLocalization.translateText(DOCUMENT_TYPES[row.doc_type]) : row.doc_type;
                    var profile = row.creator_user_profile || {};
                    var creator = profile.display_name || row.submitted_by_user_name || row.created_user_code || '';
                    var isPending = row.request_status === 'Pending';
                    // BE trả can_view = 0 khi người duyệt không mở được phiếu: không mở, không nút, ghi rõ lý do.
                    var canView = Number(row.can_view) !== 0;
                    var open = function () {
                        if (canView) onOpenDocument(row.doc_type, row.doc_code);
                    };
                    return (
                        <div
                            key={row.row_key}
                            role="button"
                            tabIndex={0}
                            className={'rs-card approval-card' + (canView ? '' : ' approval-card--locked')}
                            onClick={open}
                            onKeyDown={function (e) {
                                if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    open();
                                }
                            }}
                        >
                            <div className="rs-card-row">
                                <span className="approval-doc-icon">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </span>
                                <span className="approval-doc-heading">
                                    <span className="approval-doc-type">{typeLabel}</span>
                                    <span className="approval-doc-code">#{row.doc_code}</span>
                                </span>
                                <StatusBadge status={row.request_status} />
                            </div>
                            {(row.description || row.entity_name) && <div className="rs-card-text">{row.description || row.entity_name}</div>}
                            {canView ? (
                                <div className="approval-doc-amount">
                                    {AntLocalization.translateCurrency(Number(row.doc_amt) || 0, false, { currency: row.currency_code })}
                                </div>
                            ) : (
                                <div className="approval-doc-locked">{AntLocalization.translateText(TEXT.noViewPermission)}</div>
                            )}
                            <div className="rs-card-footer">
                                <SidebarAvatar name={creator} src={profile.avatar_full_path} />
                                <div className="rs-card-person">
                                    <span className="rs-card-person-name">{creator}</span>
                                    <span className="rs-card-person-detail">{sidebarDate(row.submitted_at, false)}</span>
                                </div>
                                {isPending && row.pending_step && (
                                    <span className="approval-doc-level">
                                        {AntLocalization.translateText(TEXT.level)} {row.pending_step}
                                    </span>
                                )}
                            </div>
                            {canDecide(row) && (
                                <div className="approval-quick-actions">
                                    <button
                                        type="button"
                                        className="approval-quick-btn approval-quick-btn--reject"
                                        disabled={!!deciding}
                                        onClick={function (e) {
                                            decide('actionRejectByRule', row, e);
                                        }}
                                    >
                                        {AntLocalization.translateText(TEXT.reject)}
                                    </button>
                                    <button
                                        type="button"
                                        className="approval-quick-btn approval-quick-btn--approve"
                                        disabled={!!deciding}
                                        onClick={function (e) {
                                            decide('actionApproveByRule', row, e);
                                        }}
                                    >
                                        {AntLocalization.translateText(TEXT.approve)}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {loading && <SidebarState type="loading" compact={rows.length > 0} />}
                {!loading && !error && hasMore && (
                    <button type="button" className="rs-more-btn" onClick={function () { load(false); }}>
                        {AntLocalization.translateText(TEXT.loadMore)}
                    </button>
                )}
            </div>
        </div>
    );
}

export default function GlobalApprovalPanel(props) {
    var navigate = useNavigate();
    var can = useCanSee();
    var tabs = [
        { key: 'requests', label: TEXT.requests, visible: can.request || can.approve },
        { key: 'documents', label: TEXT.documents, visible: can.documents }
    ].filter(function (tab) {
        return tab.visible;
    });
    var [selected, setSelected] = useState('requests');
    var current =
        tabs.find(function (tab) {
            return tab.key === selected;
        }) || tabs[0];

    var openDocument = useCallback(
        function (docType, docCode) {
            if (props.onNavigate) props.onNavigate(docType, docCode);
        },
        [props.onNavigate]
    );

    if (!current) return null;

    return (
        <div className="approval-panel">
            {tabs.length > 1 && (
                <div className="rs-segment" role="tablist">
                    {tabs.map(function (tab) {
                        var active = current.key === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                className={'rs-segment-btn' + (active ? ' active' : '')}
                                onClick={function () {
                                    setSelected(tab.key);
                                }}
                            >
                                {AntLocalization.translateText(tab.label)}
                            </button>
                        );
                    })}
                </div>
            )}
            {current.key === 'documents' ? (
                <DocumentList key="documents" onOpenDocument={openDocument} />
            ) : (
                <ProposalList
                    key="requests"
                    navigate={navigate}
                    canCreate={can.request}
                    canApprove={can.approve}
                    currentUserCode={AntAuth.getSessionInfo('user_code')}
                />
            )}
        </div>
    );
}
